import {
  Card,
  CardActions,
  CardContent,
  Collapse,
  Grid,
  IconButton,
  Input,
  Table,
  TableBody,
  Typography,
} from '@material-ui/core';
import AddCircleOutlineOutlinedIcon from '@material-ui/icons/AddCircleOutlineOutlined';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import RemoveCircleOutlineOutlinedIcon from '@material-ui/icons/RemoveCircleOutlineOutlined';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as shoppingCartActions from '~/store/legacy/actions/shoppingCartActions';
import { toPrice } from '~/utils/format';
import MealCustomization from './MealCustomization';
import Truncate from '../ui-kit/Truncate/Truncate';
import useStyles from './Meal.style';
import { debounce } from 'lodash';
import { selectMealNotes } from '~/store/legacy/selector/shoppingCartSelectors';

function flattenMealCustomization(customization) {
  const flat = {
    id: customization.id,
    quantity: customization.quantity,
    ...customization.attributes,
  };
  if (customization.relationships?.customizedProperty?.data.attributes) {
    const prop = customization.relationships?.customizedProperty?.data.attributes;
    flat.propertyName = prop.name;
    flat.propertyForeignName = prop.foreignName;
    flat.maxQuantityPerOption = prop.maxQuantityPerOption;
    flat.maxSelectedOption = prop.maxSelectedOption;
    flat.maxSelectedQuantity = prop.maxSelectedQuantity;
    flat.minSelectedOption = prop.minSelectedOption;
  }
  return flat;
}

export const MealNote = (props) => {
  const classes = useStyles();
  const { initialNote, onNoteChange } = props;
  const [note, setNote] = useState();

  useEffect(() => {
    setNote(initialNote);
  }, []);

  const debouncedOnNoteChange = useRef(
    debounce((note) => {
      onNoteChange(note);
    }, 1000)
  ).current;

  const handleChange = (e) => {
    setNote(e.target.value);
    debouncedOnNoteChange(e.target.value);
  };

  return (
    <Input
      data-locator='notes-input'
      className={classes.mealNote}
      fullWidth
      multiline
      disableUnderline
      rows={1}
      rowsMax={2}
      inputProps={{ style: { fontSize: '0.9em' } }}
      placeholder='Notes'
      value={note}
      onChange={handleChange}
    />
  );
};

const Meal = (props) => {
  const { meal } = props;
  const mealNotes = useSelector(selectMealNotes);
  const mealNote = mealNotes[meal.id] || '';
  const dispatch = useDispatch();
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const totalPrice = meal.attributes.price * meal.quantity;

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const incrementMealQuantity = () => {
    dispatch(shoppingCartActions.incrementMealQuantity({ meal }));
  };
  const decrementMealQuantity = () => {
    dispatch(shoppingCartActions.decrementMealQuantity({ meal }));
  };

  const onNoteChange = (note) => {
    dispatch(shoppingCartActions.updateMealNotes({ mealId: meal.id, newNote: note }));
  };

  const customizations = Object.keys(meal.customizationOptions).map((optId) =>
    flattenMealCustomization(meal.customizationOptions[optId])
  );

  return (
    <Card variant='outlined' className={classes.meal}>
      <CardContent className={classes.mealContent}>
        <Typography variant='h6' className={classes.name}>
          <Truncate text={meal.attributes.name} />
        </Typography>
        <Typography variant='subtitle2' className={classes.foreignName}>
          {meal.attributes.foreignName}
        </Typography>
        <div className={classes.priceline}>
          <div className={classes.price}>
            <Typography variant='h6'>{toPrice(totalPrice)}</Typography>
          </div>
          <div className={classes.quantity}>
            <IconButton
              data-locator='decrement-button'
              className={classes.quantityButton}
              aria-label={`remove one ${meal.attributes.name}`}
              title={`remove one ${meal.attributes.name}`}
              color='inherit'
              onClick={decrementMealQuantity}>
              {meal.quantity === 1 ? <HighlightOffIcon /> : <RemoveCircleOutlineOutlinedIcon />}
            </IconButton>
            <Typography
              variant='h5'
              component='span'
              className={classes.quantityDisplay}
              data-locator='quantity'>
              {meal.quantity}
            </Typography>
            <IconButton
              data-locator='increment-button'
              className={classes.quantityButton}
              aria-label={`add one ${meal.attributes.name}`}
              title={`add one ${meal.attributes.name}`}
              color='inherit'
              onClick={incrementMealQuantity}>
              <AddCircleOutlineOutlinedIcon />
            </IconButton>
          </div>
        </div>
        {customizations.length > 0 && (
          <Grid container className={classes.customizationsContainer}>
            <Grid item xs={11}>
              <Collapse collapsedHeight='40px' in={expanded}>
                <Table className={classes.customizations} size='small'>
                  <TableBody data-locator='customizations'>
                    {customizations.map((cust) => (
                      <MealCustomization
                        customization={cust}
                        key={cust.id}
                        data-locator='customization'
                      />
                    ))}
                  </TableBody>
                </Table>
              </Collapse>
              {customizations.length > 1 && (
                <div
                  className={classNames(
                    classes.customizationsCollapse,
                    expanded ? classes.expanded : null
                  )}></div>
              )}
            </Grid>
            <Grid item xs={1}>
              {customizations.length > 1 && (
                <IconButton
                  onClick={handleExpandClick}
                  aria-expanded={expanded}
                  aria-label='show more'
                  className={classNames(
                    classes.expandControl,
                    expanded ? classes.expandControlOpen : null
                  )}>
                  <ExpandMoreIcon />
                </IconButton>
              )}
            </Grid>
          </Grid>
        )}
      </CardContent>
      <CardActions className={classes.notes}>
        <MealNote initialNote={mealNote} onNoteChange={onNoteChange} />
      </CardActions>
    </Card>
  );
};

export default Meal;
